import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Paper, 
  Alert,
  Snackbar
} from '@mui/material';
import { auth, db } from '../firebase/config';
import { sendPasswordResetEmail, signInWithEmailAndPassword, deleteUser } from 'firebase/auth';
import { doc, deleteDoc } from 'firebase/firestore';

const DeleteAccountPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    
    if (password !== confirmPassword) {
      setErrorMessage('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    try {
      // Reautenticar al usuario
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Eliminar documentos del usuario en Firestore
      await deleteDoc(doc(db, 'users', user.uid));
      await deleteDoc(doc(db, 'user_profiles', user.uid));
      
      // Eliminar la cuenta de autenticación
      await deleteUser(user);
      
      setSuccessMessage('Tu cuenta ha sido eliminada correctamente');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error al eliminar cuenta:', error);
      if (error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
        setErrorMessage('Email o contraseña incorrectos');
      } else {
        setErrorMessage('Error al eliminar la cuenta. Por favor, inténtalo de nuevo más tarde o contacta con soporte.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      setErrorMessage('Por favor, introduce tu email para restablecer la contraseña');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setSuccessMessage('Se ha enviado un email para restablecer tu contraseña');
    } catch (error) {
      console.error('Error al enviar email de restablecimiento:', error);
      setErrorMessage('Error al enviar email de restablecimiento. Comprueba que el email es correcto.');
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Eliminar cuenta
        </Typography>
        
        <Typography variant="body1" paragraph sx={{ mb: 3 }}>
          Al eliminar tu cuenta, se borrarán permanentemente todos tus datos personales, 
          perfiles, mensajes y cualquier otra información asociada a tu cuenta en ToPack.
          Esta acción no se puede deshacer.
        </Typography>

        <Box component="form" onSubmit={handleDeleteAccount} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Contraseña"
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="Confirmar contraseña"
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />

          {errorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button
              variant="text"
              onClick={handleResetPassword}
              disabled={loading}
            >
              ¿Olvidaste tu contraseña?
            </Button>
            
            <Button
              type="submit"
              variant="contained"
              color="error"
              disabled={loading}
            >
              Eliminar cuenta
            </Button>
          </Box>
        </Box>
      </Paper>
      
      <Snackbar 
        open={!!successMessage} 
        autoHideDuration={6000} 
        onClose={() => setSuccessMessage('')}
      >
        <Alert severity="success">{successMessage}</Alert>
      </Snackbar>
    </Container>
  );
};

export default DeleteAccountPage; 